(() => {
  const STORAGE_KEY = "exo-recent-products-v1";

  class ExoProductDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.tabs = Array.from(this.querySelectorAll("[data-exo-discovery-tab]"));
      this.panels = Array.from(this.querySelectorAll("[data-exo-discovery-panel]"));
      this.currentProduct = this.readCurrentProduct();

      this.bindTabs();
      this.bindRailControls();
      this.renderRecentlyViewed();
      this.rememberCurrentProduct();
      this.loadRecommendations();
    }

    readCurrentProduct() {
      const source = this.querySelector("[data-exo-current-product]");
      if (!source) return null;

      try {
        return JSON.parse(source.textContent);
      } catch (error) {
        return null;
      }
    }

    readHistory() {
      try {
        const history = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
        return Array.isArray(history) ? history : [];
      } catch (error) {
        return [];
      }
    }

    bindTabs() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener("click", () => this.activatePanel(tab.dataset.exoDiscoveryTab, true));
        tab.addEventListener("keydown", (event) => {
          let targetIndex = index;
          if (event.key === "ArrowRight") targetIndex = (index + 1) % this.tabs.length;
          if (event.key === "ArrowLeft") targetIndex = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === "Home") targetIndex = 0;
          if (event.key === "End") targetIndex = this.tabs.length - 1;
          if (targetIndex === index) return;

          event.preventDefault();
          const targetTab = this.tabs[targetIndex];
          this.activatePanel(targetTab.dataset.exoDiscoveryTab, true);
        });
      });
    }

    activatePanel(name, focusTab = false) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.exoDiscoveryTab === name;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", active ? "true" : "false");
        tab.tabIndex = active ? 0 : -1;
        if (active && focusTab) tab.focus();
      });

      this.panels.forEach((panel) => {
        const active = panel.dataset.exoDiscoveryPanel === name;
        panel.classList.toggle("is-active", active);
        panel.hidden = !active;
      });
    }

    bindRailControls() {
      this.querySelectorAll("[data-exo-rail-prev], [data-exo-rail-next]").forEach((button) => {
        button.addEventListener("click", () => {
          const name = button.dataset.exoRailPrev || button.dataset.exoRailNext;
          const rail = this.querySelector(`[data-exo-discovery-rail="${name}"]`);
          if (!rail) return;
          const direction = button.hasAttribute("data-exo-rail-prev") ? -1 : 1;
          rail.scrollBy({ left: direction * Math.max(280, rail.clientWidth * 0.78), behavior: "smooth" });
        });
      });
    }

    async loadRecommendations() {
      const url = this.dataset.recommendationsUrl;
      const grid = this.querySelector("[data-exo-related-grid]");
      const status = this.querySelector("[data-exo-related-status]");
      if (!url || !grid) return;

      try {
        const response = await fetch(url, { credentials: "same-origin" });
        if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);

        const markup = await response.text();
        const documentFragment = new DOMParser().parseFromString(markup, "text/html");
        const incomingGrid = documentFragment.querySelector("[data-exo-related-grid]");
        if (!incomingGrid) throw new Error("Recommendation grid missing from response");

        grid.replaceChildren(...Array.from(incomingGrid.children));
        grid.removeAttribute("aria-busy");
        if (status) {
          status.textContent = grid.children.length
            ? ""
            : "More connected products are being prepared for this system.";
        }
      } catch (error) {
        grid.removeAttribute("aria-busy");
        grid.replaceChildren();
        if (status) status.textContent = "Related products are temporarily unavailable. Explore the full collection below.";
      }
    }

    renderRecentlyViewed() {
      const grid = this.querySelector("[data-exo-recent-grid]");
      const empty = this.querySelector("[data-exo-recent-empty]");
      if (!grid) return;

      const currentId = String(this.currentProduct?.id || "");
      const limit = Number.parseInt(this.dataset.recentLimit || "6", 10);
      const products = this.readHistory()
        .filter((item) => item && String(item.id) !== currentId)
        .slice(0, limit);

      grid.replaceChildren(...products.map((item, index) => this.buildRecentCard(item, index)));
      if (empty) empty.hidden = products.length > 0;
    }

    rememberCurrentProduct() {
      if (!this.currentProduct?.id || !this.currentProduct?.url) return;

      const currentId = String(this.currentProduct.id);
      const history = this.readHistory().filter((item) => item && String(item.id) !== currentId);
      history.unshift(this.currentProduct);

      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(0, 12)));
      } catch (error) {
        // Browsing history is an enhancement; product discovery still works without storage.
      }
    }

    buildRecentCard(item, index) {
      const link = document.createElement("a");
      link.className = "exo-discovery-card";
      link.href = item.url;
      link.setAttribute("aria-label", `View ${item.title || "product"}`);
      link.dataset.exoDiscoveryCard = "";

      const media = document.createElement("div");
      media.className = "exo-discovery-card__media";
      if (item.image) {
        const image = document.createElement("img");
        image.src = item.image;
        image.alt = item.imageAlt || item.title || "EXO product";
        image.loading = "lazy";
        image.width = 1000;
        image.height = 1000;
        media.append(image);
      } else {
        const placeholder = document.createElement("div");
        placeholder.className = "exo-discovery-card__placeholder";
        const mark = document.createElement("span");
        mark.textContent = "EXO";
        placeholder.append(mark);
        media.append(placeholder);
      }

      const position = document.createElement("span");
      position.className = "exo-discovery-card__index";
      position.textContent = String(index + 1).padStart(2, "0");
      const view = document.createElement("span");
      view.className = "exo-discovery-card__view";
      view.textContent = "RETURN TO PRODUCT";
      media.append(position, view);

      const body = document.createElement("div");
      body.className = "exo-discovery-card__body";
      const type = document.createElement("span");
      type.className = "exo-discovery-card__type";
      type.textContent = item.type || "EXO PRODUCT";
      const title = document.createElement("h3");
      title.textContent = item.title || "EXO product";
      const meta = document.createElement("div");
      meta.className = "exo-discovery-card__meta";
      const price = document.createElement("span");
      price.textContent = item.projectPricing
        ? "Project pricing"
        : item.priceVaries
          ? `From ${item.priceMin || item.price || ""}`
          : item.price || "View product";
      const arrow = document.createElement("span");
      arrow.className = "north-east-arrow";
      arrow.setAttribute("aria-hidden", "true");
      meta.append(price, arrow);
      body.append(type, title, meta);
      link.append(media, body);
      return link;
    }
  }

  if (!window.customElements.get("exo-product-discovery")) {
    window.customElements.define("exo-product-discovery", ExoProductDiscovery);
  }
})();
